import type {Optional} from '@yandex-delivery/types'
import type {CargoPointAddress} from '@yandex-delivery/types/schemas/services/cargo-claims/definitions'
import {applySpec, flow, get, isNotNull, isString, merge, toDefaultEmptyString} from '@yandex-delivery/utils'

import type {PointAddress, SddCargoPointAddress, SearchedSddClaimRoutePoint} from '../types'

const _toAddress: (routePointAddress?: SddCargoPointAddress) => PointAddress = applySpec<PointAddress>({
  building: flow(get('building'), toDefaultEmptyString),
  city: flow(get('city'), toDefaultEmptyString),
  coordinates: get('coordinates'),
  country: flow(get('country'), toDefaultEmptyString),
  doorCode: flow(get('door_code'), toDefaultEmptyString),
  flat: flow(get('sflat'), toDefaultEmptyString),
  floor: flow(get('sfloor'), toDefaultEmptyString),
  fullName: flow(get('fullname'), toDefaultEmptyString),
  shortName: flow(get('shortname'), toDefaultEmptyString),
  porch: flow(get('porch'), toDefaultEmptyString),
  street: flow(get('street'), toDefaultEmptyString),
  uri: flow(get('uri'), toDefaultEmptyString),
})

const _prepareAddressComment = (comment?: string) => {
  if (comment === undefined) {
    return {}
  }

  const additionalCommentRegexp: RegExp = /(\[.+ №\d+]\(.+\)){1,10}/g
  const photoUrlRegexp: RegExp = /\((http.+)\)/

  const commentPhotos = comment?.match(additionalCommentRegexp)

  const photos = commentPhotos
    ?.map(photo => photo.match(photoUrlRegexp))
    .filter(isNotNull)
    .map(get('[1]'))
    .filter(isString)
    .map(photoUrl => ({
      photo_id: photoUrl,
      download_url: photoUrl,
      thumbnail_url: photoUrl,
    }))

  return {
    comment: comment?.replaceAll(additionalCommentRegexp, '').replaceAll('\n', ''),
    _comment: commentPhotos?.join('\n'),
    _photos: photos,
  }
}

const _toComment: (address?: CargoPointAddress) => Optional<PointAddress> = flow(get('comment'), _prepareAddressComment)

const _toPointAddressComment: (routePoint?: SearchedSddClaimRoutePoint) => Optional<PointAddress> = flow(
  get('address'),
  _toComment,
)

const _toPointAddress: (routePoint?: SearchedSddClaimRoutePoint) => PointAddress = flow(get('address'), _toAddress)

export const toPointAddress = (routePoint?: SearchedSddClaimRoutePoint) =>
  merge(_toPointAddress(routePoint), _toPointAddressComment(routePoint))
